import { useState, useCallback } from 'react';
import { apiFetch } from '../scripts/api';
import { __ } from '@wordpress/i18n';

const STATUS_LABELS = {
	geocoded: __( 'Geocoded', 'anacrm' ),
	already_geocoded: __( 'Already geocoded', 'anacrm' ),
	not_found: __( 'Not found', 'anacrm' ),
	no_address: __( 'No address', 'anacrm' ),
	error: __( 'Error', 'anacrm' ),
};

const STATUS_CLASSES = {
	geocoded: 'bg-green text-black',
	already_geocoded: 'bg-gray-light text-black',
	not_found: 'bg-yellow text-black',
	no_address: 'bg-gray-light text-black',
	error: 'bg-red text-white',
};

// Nominatim's usage policy caps requests at 1/second; pace client-side.
const REQUEST_DELAY_MS = 1100;
const MAX_LOG_ENTRIES = 200;

function sleep( ms ) {
	return new Promise( ( resolve ) => setTimeout( resolve, ms ) );
}

export default function GeocodeRunner( { type } ) {
	const [ stage, setStage ] = useState( 'select' ); // select | running | done
	const [ force, setForce ] = useState( false );
	const [ error, setError ] = useState( null );
	const [ progress, setProgress ] = useState( { processed: 0, total: 0 } );
	const [ log, setLog ] = useState( [] );
	const [ counts, setCounts ] = useState( null );

	const reset = useCallback( () => {
		setStage( 'select' );
		setError( null );
		setProgress( { processed: 0, total: 0 } );
		setLog( [] );
		setCounts( null );
	}, [] );

	const handleStart = async () => {
		setStage( 'running' );
		setError( null );
		setLog( [] );

		try {
			const status = await apiFetch(
				`/anacrm/v1/${ type }/geocode/status`
			);
			const total = status.total;
			setProgress( { processed: 0, total } );

			const totals = {
				geocoded: 0,
				already_geocoded: 0,
				not_found: 0,
				no_address: 0,
				error: 0,
			};
			let processed = 0;
			let offset = 0;
			let needsDelay = false; // Only calls that actually hit the geocoder need pacing.

			while ( offset < total ) {
				if ( needsDelay ) {
					await sleep( REQUEST_DELAY_MS );
				}

				const result = await apiFetch(
					`/anacrm/v1/${ type }/geocode/run`,
					{
						method: 'POST',
						body: JSON.stringify( { force, offset } ),
					}
				);

				if ( result.done ) {
					break;
				}

				totals[ result.status ] = ( totals[ result.status ] ?? 0 ) + 1;
				processed++;
				offset++;
				needsDelay = !! result.apiCalled;

				setProgress( { processed, total } );
				setLog( ( prev ) =>
					[ result, ...prev ].slice( 0, MAX_LOG_ENTRIES )
				);
			}

			setCounts( totals );
			setStage( 'done' );
		} catch ( err ) {
			setError( err.message );
			setStage( 'select' );
		}
	};

	if ( stage === 'running' || stage === 'done' ) {
		const pct = progress.total
			? Math.round( ( progress.processed / progress.total ) * 100 )
			: 100;

		return (
			<div className="flex flex-col gap-4">
				<h2 className="text-xl font-bold">
					{ stage === 'running'
						? __( 'Geocoding…', 'anacrm' )
						: __( 'Geocoding complete', 'anacrm' ) }
				</h2>
				<div className="w-full h-4 border-2 border-black bg-white">
					<div
						className="h-full bg-green"
						style={ { width: `${ pct }%` } }
					/>
				</div>
				<p className="text-sm">
					{ progress.processed } { __( 'of', 'anacrm' ) }{ ' ' }
					{ progress.total } { __( 'records processed', 'anacrm' ) }
				</p>
				{ stage === 'done' && counts && (
					<div className="text-sm flex flex-wrap gap-4">
						<span>
							{ counts.geocoded } { __( 'geocoded', 'anacrm' ) }
						</span>
						<span>
							{ counts.already_geocoded }{ ' ' }
							{ __( 'already had coordinates', 'anacrm' ) }
						</span>
						<span>
							{ counts.not_found } { __( 'not found', 'anacrm' ) }
						</span>
						<span>
							{ counts.no_address }{ ' ' }
							{ __( 'without address', 'anacrm' ) }
						</span>
						<span>
							{ counts.error } { __( 'errors', 'anacrm' ) }
						</span>
					</div>
				) }
				{ log.length > 0 && (
					<div className="overflow-y-auto max-h-[400px] border-2 border-black">
						<table className="w-full border-collapse text-xs">
							<thead>
								<tr className="bg-gray-light">
									<th className="px-3 py-2 text-left">
										{ __( 'Name', 'anacrm' ) }
									</th>
									<th className="px-3 py-2 text-left">
										{ __( 'Status', 'anacrm' ) }
									</th>
									<th className="px-3 py-2 text-left">
										{ __( 'Coordinates', 'anacrm' ) }
									</th>
								</tr>
							</thead>
							<tbody>
								{ log.map( ( r ) => (
									<tr
										key={ `${ r.postId }-${ r.status }` }
										className="border-t border-gray-light"
									>
										<td className="px-3 py-2">
											{ r.postTitle }
										</td>
										<td className="px-3 py-2">
											<span
												className={ `inline-block px-2 py-0.5 font-semibold ${
													STATUS_CLASSES[ r.status ]
												}` }
											>
												{ STATUS_LABELS[ r.status ] }
											</span>
										</td>
										<td className="px-3 py-2">
											{ r.status === 'geocoded'
												? `${ r.lat }, ${ r.lng }`
												: r.message ?? '' }
										</td>
									</tr>
								) ) }
							</tbody>
						</table>
					</div>
				) }
				{ stage === 'done' && (
					<div>
						<button
							onClick={ reset }
							className="px-4 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold text-sm"
						>
							{ __( 'Back', 'anacrm' ) }
						</button>
					</div>
				) }
			</div>
		);
	}

	// stage === 'select'
	return (
		<div className="flex flex-col gap-4">
			<h2 className="text-xl font-bold">
				{ __( 'Geocode records', 'anacrm' ) }
			</h2>
			<p className="text-sm">
				{ __(
					'Looks up latitude/longitude coordinates from each record’s address, city, region and territory, using OpenStreetMap’s Nominatim geocoder. By default only records without existing coordinates are processed, so it is safe to re-run after a bulk import.',
					'anacrm'
				) }
			</p>
			<label
				htmlFor="anacrm-geocode-force"
				className="flex items-center gap-2 text-sm"
			>
				<input
					id="anacrm-geocode-force"
					type="checkbox"
					checked={ force }
					onChange={ ( e ) => setForce( e.target.checked ) }
				/>
				{ __(
					'Force re-geocode all records, including ones that already have coordinates',
					'anacrm'
				) }
			</label>
			{ error && (
				<div className="px-4 py-3 border-2 border-red text-red text-sm">
					{ error }
				</div>
			) }
			<div>
				<button
					onClick={ handleStart }
					className="px-4 py-2 border-2 border-green-dark bg-green hover:bg-yellow cursor-pointer font-bold text-sm"
				>
					{ __( 'Start', 'anacrm' ) }
				</button>
			</div>
		</div>
	);
}
